import React, { useEffect, useRef, useState } from "react";
import { Search, Rocket, Target, RefreshCw, TrendingUp, ShieldCheck } from "lucide-react";

/**
 * "How We Build" — two centered zigzag roads side by side:
 * Methodology (left column) and Process (right column).
 */

const ROW_HEIGHT = 190;
const VIEW_W = 320;
const CENTER_X = VIEW_W / 2;
const AMPLITUDE = 60;

const METHODOLOGY_STEPS = [
  { id: "M1", title: "Product Discovery", description: "Understand business goals, user pain points, and market opportunities before development begins.", icon: Search, color: "#2563EB" },
  { id: "M2", title: "Launch Prioritization", description: "Focus on MVP features that create value quickly and reduce development risk.", icon: Rocket, color: "#0891B2" },
  { id: "M3", title: "User Focused Roadmap", description: "Build product strategies around user needs, feedback, and business objectives.", icon: Target, color: "#0D9488" },
  { id: "M4", title: "Agile Iteration", description: "Deliver continuous improvements through short development cycles and testing.", icon: RefreshCw, color: "#059669" },
  { id: "M5", title: "Scale & Growth", description: "Optimize performance, security, and scalability for sustainable growth.", icon: TrendingUp, color: "#16A34A" },
  { id: "M6", title: "Continuous Optimization", description: "Refine performance, reliability, and user experience with ongoing feedback loops.", icon: ShieldCheck, color: "#8B5CF6" },
];

const PROCESS_STEPS = [
  { id: "01", title: "Discovery", description: "Conceptualize and shape the idea until the goal is unambiguous.", color: "#F97316" },
  { id: "02", title: "Design", description: "Lock the architecture and the tech stack the build will stand on.", color: "#EF4444" },
  { id: "03", title: "Prototype", description: "Iterate on a working MVP fast enough to test real assumptions.", color: "#06B6D4" },
  { id: "04", title: "Build", description: "Full development paired with continuous testing, not bolted on after.", color: "#EC4899" },
  { id: "05", title: "Deploy", description: "Automated pipelines carry the release out with a clean go-live.", color: "#3B82F6" },
  { id: "06", title: "Support", description: "Monitor, scale, and keep improving once real users show up.", color: "#22C55E" },
];

function buildZigzagPath(n, mirror) {
  let d = `M ${CENTER_X} 0`;
  for (let i = 0; i < n; i++) {
    const y0 = i * ROW_HEIGHT;
    const y1 = (i + 1) * ROW_HEIGHT;
    const bulgesRight = mirror ? i % 2 !== 0 : i % 2 === 0;
    const cx = bulgesRight ? CENTER_X + AMPLITUDE : CENTER_X - AMPLITUDE;
    d += ` C ${cx} ${y0 + ROW_HEIGHT * 0.3}, ${cx} ${y0 + ROW_HEIGHT * 0.7}, ${CENTER_X} ${y1}`;
  }
  return d;
}

// Fail-safe, high-performance middle progress tracker
function useMiddleProgress(ref) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let ticking = false;

    const updateProgress = () => {
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 800;
        const middle = vh / 2;
        const p = (middle - rect.top) / rect.height;
        setProgress(Math.min(1, Math.max(0, p)));
      }
      ticking = false;
    };

    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateProgress);
      }
    };

    // Calculate immediately on mount
    updateProgress();

    // { capture: true } catches scroll events even inside custom scroll containers
    window.addEventListener("scroll", onScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize, { capture: true });
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [ref]);

  return progress;
}

function VerticalRoad({ eyebrow, heading, steps, numbered, mirror }) {
  const roadRef = useRef(null);
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const progress = useMiddleProgress(trackRef);
  const [points, setPoints] = useState([]);
  const [pathLength, setPathLength] = useState(0);
  const [visible, setVisible] = useState(() => steps.map(() => false));

  const viewH = steps.length * ROW_HEIGHT;
  const pathD = buildZigzagPath(steps.length, mirror);
  const gradId = `grad-${heading.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    if (!roadRef.current) return;
    const total = roadRef.current.getTotalLength();
    setPathLength(total);
    const fractions = steps.map((_, i) => (i + 0.5) / steps.length);
    setPoints(fractions.map((f) => roadRef.current.getPointAtLength(f * total)));
  }, [steps, pathD, mirror]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            steps.forEach((_, i) => {
              setTimeout(() => {
                setVisible((v) => {
                  const next = [...v];
                  next[i] = true;
                  return next;
                });
              }, i * 140);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [steps]);

  // Dynamic calculations with zero-lag fallback
  const totalLength = pathLength || (roadRef.current ? roadRef.current.getTotalLength() : 0);
  const dashOffset = totalLength * (1 - progress);
  const carPoint =
    roadRef.current && totalLength > 0
      ? roadRef.current.getPointAtLength(progress * totalLength)
      : null;

  // Safe zones so label text never collides with the road's bulge.
  const safeGapPct = ((AMPLITUDE + 14) / VIEW_W) * 100; // ~23%
  const leftEdge = 50 - safeGapPct;
  const rightEdge = 50 + safeGapPct;

  return (
    <div ref={wrapRef}>
      <div className="mb-10 text-center">
        <span className="text-sm md:text-base font-semibold tracking-[0.32em] uppercase font-mono" style={{ color: steps[0].color }}>
          {eyebrow}
        </span>
        <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-2 text-slate-900">
          {heading}
        </h3>
      </div>

      <div ref={trackRef} className="relative" style={{ height: viewH }}>
        {/* Centered road */}
        <svg
          viewBox={`0 0 ${VIEW_W} ${viewH}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
              {steps.map((s, i) => (
                <stop key={s.id} offset={`${(i / (steps.length - 1)) * 100}%`} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>

          {/* Base gray road */}
          <path d={pathD} fill="none" stroke="#E2E5EA" strokeWidth="14" strokeLinecap="round" />

          {/* Hidden reference path for measurements */}
          <path ref={roadRef} d={pathD} fill="none" stroke="none" />

          {/* Dynamic filled progress path (100% sync with pointer) */}
          {totalLength > 0 && (
            <path
              d={pathD}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={totalLength}
              strokeDashoffset={dashOffset}
            />
          )}

          {/* Center dashed road line */}
          <path d={pathD} fill="none" stroke="white" strokeWidth="2" strokeDasharray="8 9" strokeLinecap="round" opacity="0.85" />

          {/* Tracker ball pin */}
          {carPoint && (
            <circle cx={carPoint.x} cy={carPoint.y} r="6" fill="white" stroke="#0F172A" strokeWidth="2" />
          )}
        </svg>

        {points.map((pt, i) => {
          const step = steps[i];
          const Icon = step.icon;
          const topPct = (pt.y / viewH) * 100;
          const xPct = (pt.x / VIEW_W) * 100;
          const parityLeft = i % 2 === 0;
          const side = mirror ? (parityLeft ? "right" : "left") : parityLeft ? "left" : "right";
          const reached = progress >= (i + 0.5) / steps.length - 0.03;
          const labelEdge = side === "left" ? leftEdge : rightEdge;

          return (
            <React.Fragment key={step.id}>
              {/* Icon marker */}
              <div
                className="absolute z-10"
                style={{ top: `${topPct}%`, left: `${xPct}%`, transform: "translate(-50%, -50%)" }}
              >
                <div
                  className={`mtd-reveal w-10 h-10 rounded-full flex items-center justify-center shadow-md ring-4 ring-[#F7F8FA] transition-all duration-300 ${
                    visible[i] ? "opacity-100 scale-100" : "opacity-0 scale-50"
                  }`}
                  style={{ backgroundColor: reached ? step.color : "#CBD5E1" }}
                >
                  {numbered ? (
                    <span className="text-[13px] font-bold text-white">{step.id}</span>
                  ) : (
                    <Icon size={16} color="white" />
                  )}
                </div>
              </div>

              {/* Connector line */}
              <div
                className="absolute h-px bg-slate-300"
                style={{
                  top: `${topPct}%`,
                  left: side === "left" ? `${labelEdge}%` : `${xPct}%`,
                  width: `${Math.abs(xPct - labelEdge)}%`,
                  transform: "translateY(-50%)",
                }}
              />

              {/* Plain-text label */}
              <div
                className={`mtd-reveal absolute transition-all duration-500 ${
                  visible[i] ? "opacity-100 translate-x-0" : `opacity-0 ${side === "left" ? "translate-x-3" : "-translate-x-3"}`
                }`}
                style={{
                  top: `${topPct}%`,
                  ...(side === "left" ? { right: `${100 - leftEdge}%`, textAlign: "right" } : { left: `${rightEdge}%`, textAlign: "left" }),
                  width: `${leftEdge - 2}%`,
                  transform: "translateY(-50%)",
                  transitionDelay: `${i * 90 + 80}ms`,
                }}
              >
                <div className="group inline-block">
                  <h4 className="font-semibold text-slate-900 leading-tight tracking-tight transition-colors duration-300 group-hover:opacity-80">
                    {step.title}
                  </h4>
                  <p className="mt-1 text-[15px] leading-7 text-slate-600">
                    {step.description}
                  </p>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function MobileList({ heading, eyebrow, steps, numbered }) {
  return (
    <div>
      <div className="mb-6">
        <span className="text-sm md:text-base font-semibold tracking-[0.32em] uppercase font-mono" style={{ color: steps[0].color }}>
          {eyebrow}
        </span>
        <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-2 text-slate-900">{heading}</h3>
      </div>
      <div className="relative pl-9">
        <div
          className="absolute left-[15px] top-1 bottom-1 w-[3px] rounded-full"
          style={{ background: `linear-gradient(to bottom, ${steps.map((s) => s.color).join(",")})` }}
        />
        <div className="space-y-5">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id} className="relative">
                <div
                  className="absolute -left-9 w-8 h-8 rounded-full flex items-center justify-center shadow ring-4 ring-white"
                  style={{ backgroundColor: step.color }}
                >
                  {numbered ? (
                    <span className="text-[11px] font-bold text-white">{step.id}</span>
                  ) : (
                    <Icon size={14} color="white" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold tracking-tight text-slate-900">{step.title}</h4>
                  <p className="mt-1 text-[15px] leading-7 text-slate-600">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Methodology() {
  return (
    <section className="relative py-28 bg-[#F7F8FA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-sm md:text-base font-semibold tracking-[0.32em] uppercase font-mono text-[#355dff]">
            Our Methodology &amp; Process
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
            How We Build
          </h2>

          <div className="w-24 h-1 bg-blue-600 mx-auto mt-5 rounded-full"></div>

          <p className="max-w-4xl mx-auto mt-8 text-lg text-gray-600 leading-relaxed">
            We combine a clear delivery roadmap with a disciplined execution
            process to turn ambitious ideas into scalable digital products.
          </p>
        </div>

        <div className="hidden lg:grid grid-cols-2 gap-16 relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2" />
          <VerticalRoad eyebrow="Our Approach" heading="Methodology" steps={METHODOLOGY_STEPS} numbered={false} mirror />
          <VerticalRoad eyebrow="Our Process" heading="Steps" steps={PROCESS_STEPS} numbered={true} />
        </div>

        <div className="lg:hidden space-y-16">
          <MobileList eyebrow="Our Approach" heading="Methodology" steps={METHODOLOGY_STEPS} numbered={false} />
          <MobileList eyebrow="Our Process" heading="Steps" steps={PROCESS_STEPS} numbered={true} />
        </div>
      </div>
    </section>
  );
}